import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import apiClient from "@/lib/api-client";
import { guideSignals } from "@/lib/guideSignals";
import { trackEvent } from "@/lib/engagement";

/**
 * Shared helper to append a highlight to the `["highlights", paperId]` cache.
 * Used by both the standard `createMutation` and the evidence-stash path
 * (see `docs/superpowers/plans/2026-04-19-evidence-attach-rects.md`) so the
 * cache-write shape cannot drift between them.
 */
export function insertHighlightInCache(
  qc: QueryClient,
  paperId: string,
  highlight: PaperHighlight,
): void {
  qc.setQueryData<PaperHighlight[]>(["highlights", paperId], (old) =>
    old ? [...old, highlight] : [highlight],
  );
}

export interface PaperHighlight {
  id: string;
  text: string;
  page: number | null;
  rects: Array<Record<string, number>> | null; // PDF rects or HTML anchor [{charOffset: N}]
  source: "in_app" | "imported" | "evidence";
  has_citation: boolean | null;
  created_at: string;
}

interface CreateHighlightData {
  text: string;
  page: number | null;
  rects: Array<Record<string, number>> | null; // PDF rects or HTML anchor [{charOffset: N}]
  source?: "in_app" | "imported" | "evidence";
}

export function useHighlights(paperId: string | undefined, conceptId?: string) {
  const qc = useQueryClient();
  const queryKey = ["highlights", paperId];

  const query = useQuery({
    queryKey,
    queryFn: () => apiClient.get(`api/papers/${paperId}/highlights`).json<PaperHighlight[]>(),
    enabled: !!paperId,
    staleTime: 30_000,
  });

  // Selection state: local state is the source of truth.
  // Initialized from server (papers cache), then all changes are local.
  // PATCH mutations sync to server in the background (fire-and-forget).
  const [localSelection, setLocalSelection] = useState<string[]>([]);
  const initializedForPaper = useRef<string | null>(null);

  // Initialize from server when paper changes
  useEffect(() => {
    if (!conceptId || !paperId || paperId === initializedForPaper.current) return;
    initializedForPaper.current = paperId;
    // Read from papers query cache
    for (const role of ["foundational", "trawled"]) {
      const data = qc.getQueryData<{ papers: Array<{ id: string; selected_highlight_ids?: string[] | null }> }>(
        ["papers", conceptId, role],
      );
      const paper = data?.papers?.find((p) => p.id === paperId);
      if (paper?.selected_highlight_ids) {
        setLocalSelection(paper.selected_highlight_ids);
        return;
      }
    }
    setLocalSelection([]);
  }, [conceptId, paperId, qc]);

  const selectedIds = localSelection;

  // Fire-and-forget PATCH to sync selection to server
  const syncSelection = useCallback((newSelection: string[]) => {
    setLocalSelection(newSelection);
    if (conceptId && paperId) {
      apiClient.patch(`api/concepts/${conceptId}/papers/${paperId}/selected-highlights`, {
        json: { selected_highlight_ids: newSelection },
      }).catch(() => { /* server sync failed, local state still correct */ });
    }
  }, [conceptId, paperId]);

  const createMutation = useMutation({
    mutationFn: (data: CreateHighlightData) =>
      apiClient.post(`api/papers/${paperId}/highlights`, {
        json: { ...data, concept_id: conceptId },  // Server atomically appends to selection
      }).json<PaperHighlight>(),
    onMutate: async (data) => {
      await qc.cancelQueries({ queryKey });
      const prev = qc.getQueryData<PaperHighlight[]>(queryKey);
      const tempId = `temp-${Date.now()}`;
      const optimistic: PaperHighlight = {
        id: tempId,
        text: data.text,
        page: data.page,
        rects: data.rects,
        source: data.source ?? "in_app",
        has_citation: null,
        created_at: new Date().toISOString(),
      };
      qc.setQueryData<PaperHighlight[]>(queryKey, (old) => (old ? [...old, optimistic] : [optimistic]));
      // Optimistically add to local selection
      if (conceptId) {
        setLocalSelection((prev) => [...prev, tempId]);
      }
      return { prev, tempId };
    },
    onError: (_err, _vars, context) => {
      if (context?.prev) qc.setQueryData(queryKey, context.prev);
    },
    onSettled: (newHighlight, err, _vars, context) => {
      if (newHighlight && !err && context?.tempId) {
        // Swap temp highlight with real one — no refetch, no flash
        qc.setQueryData<PaperHighlight[]>(queryKey, (old) =>
          old?.map((h) => h.id === context.tempId ? newHighlight : h),
        );
        const updatedSelection = localSelection.map((id) => id === context.tempId ? newHighlight.id : id);
        setLocalSelection(updatedSelection);
        // Track highlight creation for guide milestones
        if (conceptId) {
          trackEvent("highlight_created", { paper_id: paperId, source: newHighlight.source });
          guideSignals.mark(conceptId, 'hasHighlights');
        }
        // Sync papers cache so revisiting this paper rehydrates correct selection
        if (conceptId) {
          for (const role of ["foundational", "trawled"]) {
            qc.setQueryData<{ papers: Array<{ id: string; selected_highlight_ids?: string[] | null }> }>(
              ["papers", conceptId, role],
              (old) => {
                if (!old) return old;
                return {
                  ...old,
                  papers: old.papers.map((p) =>
                    p.id === paperId ? { ...p, selected_highlight_ids: updatedSelection } : p,
                  ),
                };
              },
            );
          }
        }
      } else {
        // Error or unexpected state — refetch to reconcile
        qc.invalidateQueries({ queryKey });
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (highlightId: string) =>
      apiClient.delete(`api/papers/${paperId}/highlights/${highlightId}`),
    onMutate: async (highlightId) => {
      await qc.cancelQueries({ queryKey });
      const prev = qc.getQueryData<PaperHighlight[]>(queryKey);
      qc.setQueryData<PaperHighlight[]>(queryKey, (old) => old?.filter((h) => h.id !== highlightId));
      return { prev };
    },
    onError: (_err, _vars, context) => {
      if (context?.prev) qc.setQueryData(queryKey, context.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey }),
  });

  const extractMutation = useMutation({
    mutationFn: () =>
      apiClient
        .post(`api/concepts/${conceptId}/papers/${paperId}/extract-highlights`)
        .json<PaperHighlight[]>(),
    onSettled: () => qc.invalidateQueries({ queryKey }),
  });

  const toggleSelection = useCallback(
    (highlightId: string, forceAdd?: boolean) => {
      const isSelected = selectedIds.includes(highlightId);
      const updated =
        forceAdd || !isSelected
          ? [...selectedIds, highlightId]
          : selectedIds.filter((id) => id !== highlightId);
      syncSelection(updated);
    },
    [selectedIds, syncSelection],
  );

  const selectAll = useCallback(() => {
    const all = query.data?.map((h) => h.id) ?? [];
    syncSelection(all);
  }, [query.data, syncSelection]);

  const clearAll = useCallback(() => {
    syncSelection([]);
  }, [syncSelection]);

  return {
    highlights: query.data ?? [],
    isLoading: query.isLoading,
    createHighlight: createMutation.mutate,
    deleteHighlight: deleteMutation.mutate,
    extractHighlights: extractMutation.mutate,
    isExtracting: extractMutation.isPending,
    toggleSelection,
    selectAll,
    clearAll,
    selectedIds,
  };
}
